/**
 * Welke projecten meedoen in dit gesprek, als uitklapmenu naast het invoerveld.
 *
 * FRANKS OPDRACHT VAN 23 AUGUSTUS 2026, EN DE REDEN STAAT IN ZIJN EIGEN WOORDEN:
 * "achttien pillen over acht regels duwen het hele scherm weg en het is
 * onoverzichtelijk". De voorganger (`ArchiveSwitches`) zette elke regel als
 * losse pil boven de invoer. Met vier `default_off`-banken viel dat niet op;
 * sinds R47 stap 2 zijn het 18 PROJECTEN en dan is het een muur.
 *
 * DE PILLENBALK IS VERVANGEN EN NIET AANGEVULD, ook op Franks besluit. Een
 * component zonder aanroeper laten staan is precies de dode code waar we in de
 * nacht van 22 op 23 augustus drie keer over vielen (de lege `in_()`, de
 * lengtecontrole in `zetOm`, een tweede `move_to_end`).
 *
 * WAAROM DE RADIX-PRIMITIEF EN NIET ONZE EIGEN `ui/dropdown-menu`. Die wikkelt
 * `CheckboxItem` met een vast `Check`-pictogram in de indicator. Radix toont die
 * indicator OOK bij `indeterminate`, dus een half aangezet project zou daar een
 * vinkje krijgen: precies het verschil dat dit menu zichtbaar moet maken. De
 * primitief rechtstreeks gebruiken geeft de juiste rol en `aria-checked="mixed"`
 * gratis, en laat het merkteken aan ons. De gedeelde wikkel is met opzet NIET
 * aangepast: daar hangen andere menu's aan.
 *
 * DRIE STANDEN EN NIET TWEE. Half aan ontstaat zodra er een bank bij een project
 * komt nadat het is aangezet. De oude pil keek met `every` en toonde dat als
 * UIT, terwijl die ene bank wel degelijk in `include_bases` meegaat. Hier is het
 * een streepje, en de knop ernaast telt hem mee.
 */

import { useState } from "react";
import * as Menu from "@radix-ui/react-dropdown-menu";
import { Layers } from "lucide-react";

import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { samenvatting } from "../lib/switch-labels";
import { alAllesAan } from "../lib/switch-toggle";
import { botsingsRand, veiligeRand } from "../lib/veiligerand";
import { useTaal } from "@/contexts/TaalContext";

// DE WOORDEN HOREN BIJ HET SCHERM EN NIET BIJ DE GEGEVENS. Zonder gekozen
// project zijn de regels projecten, daarbinnen banken, en dat is aan een regel
// niet te zien: `Nice Tea` is een project met precies een bank.
const WOORDEN = {
  project: { enkel: "project", meer: "projecten" },
  bank: { enkel: "bank", meer: "banken" },
};

/**
 * Het merkteken voor de drie standen. Geen emoji als icoon, nergens: een
 * getekend vierkantje dat een vinkje of een streepje krijgt.
 */
export function Vakje({ stand }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="shrink-0"
      aria-hidden="true"
    >
      <rect x="2" y="2" width="12" height="12" rx="2.5" />
      {stand === "aan" && <path d="M5 8.2 7.2 10.4 11 5.8" strokeLinecap="round" />}
      {stand === "half" && <path d="M5 8h6" strokeLinecap="round" />}
    </svg>
  );
}

/**
 * `bases` zijn de regels (`{id, name, base_ids, label}`), `on` de BANK-ids die
 * aan staan, `soort` bepaalt het woord op de knop.
 *
 * `onToggle` krijgt de bank-ids van de aangeklikte regel, precies zoals de pil
 * dat deed; wat er in `include_bases` gaat zijn en blijven bank-ids.
 * `onAllOff` bestaat apart omdat achttien keer `onToggle` aanroepen op een
 * verouderde lijst zou rekenen.
 */
export default function SwitchMenu({
  bases = [],
  on = [],
  onToggle,
  onAllOff,
  onAllOn,
  soort = "project",
}) {
  const { t } = useTaal();
  // DE RAND WORDT GEMETEN BIJ HET OPENEN (glasronde 0.9.186): de veilige zone van
  // de iPhone hoort erbij, anders schuift het menu over de statusbalk.
  const [rand, setRand] = useState(() => botsingsRand());
  // NIETS TONEN ALS ER NIETS TE KIEZEN VALT, ongewijzigd overgenomen van de pil.
  // Een knop die een leeg menu opent is erger dan geen knop.
  if (!bases.length) return null;

  const woorden = WOORDEN[soort] || WOORDEN.project;
  // GL211-2: een VASTE regel is een eigen bank van het project van dit gesprek. Die weegt altijd
  // mee, dus telt zij mee in de stand op de knop; aan- en uitzetten kan zij niet.
  const idsVan = (kb) => (kb.base_ids && kb.base_ids.length ? kb.base_ids : [kb.id]);
  const schakelbaar = bases.filter((kb) => !kb.vast);
  const stand = samenvatting(bases, [...on, ...bases.filter((kb) => kb.vast).flatMap(idsVan)], woorden);
  const losAan = samenvatting(schakelbaar, on, woorden).aantal;
  const aan = new Set(on);

  return (
    <Menu.Root onOpenChange={(open) => { if (open) setRand(botsingsRand(veiligeRand())); }}>
      <Menu.Trigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          // DE HELE STAND IN DE ZWEEFTEKST, want dat is wat een knop kan wat
          // een balk niet kan: het volle verhaal opvraagbaar houden zonder het
          // permanent te tonen. Ook als `aria-label`, anders is dit voor een
          // schermlezer een knop met een cijfer erop.
          title={stand.toelichting}
          aria-label={stand.toelichting}
          data-testid="switch-menu-trigger"
          className={cn(
            "tap-target h-8 gap-1.5 rounded-lg px-2 flex-shrink-0",
            stand.aantal > 0
              ? "text-[hsl(var(--accent))]"
              : "text-muted-foreground"
          )}
        >
          <Layers className="h-4 w-4 shrink-0" aria-hidden="true" />
          {/* AFKAPPEN IN CSS EN NIET IN DE FUNCTIE, zodat de zweeftekst de
              volledige naam houdt. `Foodservice Leimuiden` mag de knop niet
              breed maken; hij mag er ook niet half in verdwijnen. */}
          {stand.tekst && (
            <span className="max-w-[7rem] truncate text-xs">{stand.tekst}</span>
          )}
        </Button>
      </Menu.Trigger>

      <Menu.Portal>
        <Menu.Content
          align="start"
          sideOffset={6}
          collisionPadding={rand}
          data-testid="switch-menu"
          // DE HOOGTE IS DE RUIMTE DIE ER ECHT IS (glasronde 0.9.186). Hier stond
          // een vaste `max-h-[60vh]`: bij een knop onderaan is 60 procent van het
          // venster meer dan de ruimte erboven, dus het menu stak boven het venster
          // uit en "Zoek overal" bovenaan viel als eerste weg. Radix geeft de
          // beschikbare hoogte binnen de rand (inclusief veilige zone) als variabele.
          // De kop staat vast; alleen de lijst eronder schuift.
          style={{ maxHeight: "var(--radix-dropdown-menu-content-available-height)" }}
          className="z-50 flex w-72 flex-col overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <div className="shrink-0" data-testid="switch-menu-kop">
          <Menu.Label className="px-2 py-1.5 text-xs text-muted-foreground">
            {t("Telt mee in dit gesprek")}
          </Menu.Label>

          {/* ZOEK OVERAL (R47 stap 3, Frank 21 okt): alles voor DIT gesprek aan in
              een keer, als tegenhanger van "Alles uit". Weg zodra alles al aan
              staat, want dan kan de knop niets doen. Globaal maken blijft
              afgewezen: dit geldt alleen voor dit gesprek. */}
          {onAllOn && schakelbaar.length > 0 && !alAllesAan(schakelbaar, on) && (
            <>
              <Menu.Item
                onSelect={() => onAllOn()}
                data-testid="switch-menu-zoek-overal"
                className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm font-medium outline-none transition-colors focus:bg-accent focus:text-accent-foreground"
              >
                {t("Zoek overal")}
              </Menu.Item>
              <Menu.Separator className="my-1 h-px bg-border" />
            </>
          )}
          </div>

          {/* DE BANKEN SCHUIVEN IN EEN EIGEN VAK. `min-h-0` is nodig, anders krimpt
              een flex-kind niet onder zijn inhoud en duwt het de kop alsnog weg. */}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden" data-testid="switch-menu-lijst">

          {bases.map((kb) => {
            const ids = idsVan(kb);
            const mee = ids.filter((id) => aan.has(id)).length;
            const vakje = kb.vast ? "aan" : mee === 0 ? "uit" : mee === ids.length ? "aan" : "half";

            if (kb.vast) return (
              <Menu.CheckboxItem key={kb.id} checked disabled data-testid="switch-menu-vast"
                className="relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none opacity-80">
                <Vakje stand="aan" />
                <span className="flex-1 truncate">{kb.name}</span>
                <span className="shrink-0 text-xs opacity-70">{t("dit project")}</span>
              </Menu.CheckboxItem>
            );

            return (
              <Menu.CheckboxItem
                key={kb.id}
                checked={
                  vakje === "aan" ? true : vakje === "half" ? "indeterminate" : false
                }
                // HET MENU MAG NIET DICHTKLAPPEN BIJ ELKE KLIK. Radix sluit
                // standaard op `select`; zonder dit moet je voor drie projecten
                // drie keer openen. Radix roept `onCheckedChange` ook aan
                // wanneer `select` is tegengehouden, dus het omzetten blijft.
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => onToggle && onToggle(ids)}
                className="relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground"
              >
                <Vakje stand={vakje} />
                <span className="flex-1 truncate">{kb.name}</span>
                {kb.label && (
                  <span className="shrink-0 text-xs opacity-70">{kb.label}</span>
                )}
              </Menu.CheckboxItem>
            );
          })}

          {/* ALLEEN ALS ER IETS AAN STAAT. Met achttien regels is vijf keer
              terugklikken anders het enige wat er is; met nul aan is het een
              knop die niets kan doen. */}
          {losAan > 0 && (
            <>
              <Menu.Separator className="my-1 h-px bg-border" />
              <Menu.Item
                onSelect={() => onAllOff && onAllOff()}
                className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground"
              >
                {t("Alles uit")}
              </Menu.Item>
            </>
          )}
          </div>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
